import React, { useRef, useState } from "react";
import { useAuth } from "../../lib/auth";
import { api, formatApiError, fileUrl } from "../../lib/api";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ProfileSettings() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [timezone, setTimezone] = useState(user?.timezone || "UTC");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/me", { name, timezone });
      await refresh();
      toast.success("Profile updated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post("/uploads/profile-picture", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Photo updated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-8" data-testid="profile-settings">
      <div>
        <h2 className="text-xl font-medium text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>Profile</h2>
        <p className="text-sm text-zinc-500 mt-1">How you appear in Milkath.</p>
      </div>

      <div className="flex items-center gap-5">
        <div className="relative h-20 w-20 rounded-full bg-zinc-800 overflow-hidden flex items-center justify-center text-2xl text-zinc-300">
          {user?.picture ? (
            <img src={fileUrl(user.picture)} alt="" className="h-full w-full object-cover" />
          ) : (user?.name?.charAt(0).toUpperCase() || "?")}
          {uploading && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-white" /></div>}
        </div>
        <div>
          <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 bg-zinc-900 border border-zinc-800 hover:border-zinc-700 px-3 py-1.5 rounded-lg text-sm text-zinc-200" data-testid="profile-upload-btn">
            <Camera className="h-3.5 w-3.5" /> Change photo
          </button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={onUpload} />
          <p className="text-xs text-zinc-500 mt-2">PNG, JPG, WebP up to 5 MB.</p>
        </div>
      </div>

      <form onSubmit={save} className="space-y-5 max-w-md">
        <div>
          <label className="text-xs font-medium text-zinc-400">Email</label>
          <input value={user?.email || ""} disabled className="mt-1.5 w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-400" />
        </div>
        <div>
          <label className="text-xs font-medium text-zinc-400">Display name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-orange-600/40 focus:border-orange-600" data-testid="profile-name-input" />
        </div>
        <div>
          <label className="text-xs font-medium text-zinc-400">Timezone</label>
          <input value={timezone} onChange={(e) => setTimezone(e.target.value)} className="mt-1.5 w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-orange-600/40 focus:border-orange-600" data-testid="profile-tz-input" />
        </div>
        <button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium" data-testid="profile-save-btn">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </form>
    </div>
  );
}
